import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Users, Edit, Trash2, ArrowLeft, Shield, User, Building2, Mail, Copy, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [invitationLink, setInvitationLink] = useState('');
  const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      setFilteredUsers(users.filter(u => 
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term) ||
        u.customer_name?.toLowerCase().includes(term)
      ));
    } else {
      setFilteredUsers(users);
    }
  }, [users, searchTerm]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Sind Sie sicher?')) return;
    
    try {
      await axios.delete(`${API}/superadmin/users/${userId}`);
      toast.success('Benutzer gelöscht');
      fetchUsers();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Löschen';
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Löschen');
    }
  };

  const handleEdit = (user) => {
    setEditUser({ ...user, password: '' });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      const data = { ...editUser };
      if (!data.password) delete data.password;
      
      await axios.put(`${API}/superadmin/users/${editUser.id}`, data);
      toast.success('Benutzer aktualisiert');
      setEditDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error('Fehler beim Speichern');
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      superadmin: 'bg-purple-100 text-purple-800',
      admin: 'bg-blue-100 text-blue-800',
      employee: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      superadmin: 'Superadmin',
      admin: 'Admin',
      employee: 'Mitarbeiter'
    };
    return <Badge className={styles[role]}>{labels[role]}</Badge>;
  };

  const getRoleIcon = (role) => {
    if (role === 'superadmin') return <Shield className="h-6 w-6 text-purple-600" />;
    if (role === 'admin') return <Building2 className="h-6 w-6 text-blue-600" />;
    return <User className="h-6 w-6 text-gray-600 dark:text-gray-400" />;
  };

  const getStatusBadge = (user) => {
    // Check onboarding status
    if (user.status === 'pending' || !user.has_password) {
      return <Badge className="bg-orange-100 text-orange-800">⏳ Einladung ausstehend</Badge>;
    }
    if (user.role === 'admin' || user.role === 'stationsleitung') {
      if (!user.has_location) {
        return <Badge className="bg-yellow-100 text-yellow-800">📍 Ohne Standort</Badge>;
      }
      if (!user.has_active_subscription) {
        return <Badge className="bg-blue-100 text-blue-800">💳 Ohne Abo</Badge>;
      }
    }
    if (!user.is_active) {
      return <Badge variant="destructive">🔒 Gesperrt</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800">✓ Aktiv</Badge>;
  };

  const handleResendInvitation = async (user) => {
    try {
      const response = await axios.post(`${API}/superadmin/resend-invitation/${user.id}`);
      setInvitationLink(response.data.invitation_link);
      setSelectedUser(user);
      setInvitationDialogOpen(true);
      toast.success('Einladungslink neu generiert');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Fehler beim Senden der Einladung';
      toast.error(msg);
    }
  };

  const copyInvitationLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(invitationLink);
        toast.success('Link kopiert!');
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = invitationLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          textArea.remove();
          toast.success('Link kopiert!');
        } catch (err) {
          textArea.remove();
          toast.error('Bitte Link manuell kopieren.');
        }
      }
    } catch (err) {
      toast.error('Bitte Link manuell kopieren.');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/superadmin/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
              Benutzerverwaltung
            </h1>
            <p className="text-muted-foreground mt-1">Alle Benutzer im System</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Suchen..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">Keine Benutzer gefunden</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map((user) => (
              <Card key={user.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                        {getRoleIcon(user.role)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{user.name}</h3>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {getRoleBadge(user.role)}
                          {getStatusBadge(user)}
                          {user.customer_name && (
                            <span className="text-xs text-muted-foreground">
                              • {user.customer_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {(user.status === 'pending' || !user.has_password) && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
                          onClick={() => handleResendInvitation(user)}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Link erneut senden
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleEdit(user)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user.role !== 'superadmin' && (
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(user.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Benutzer bearbeiten</DialogTitle>
          </DialogHeader>
          {editUser && (
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={editUser.name || ''} 
                  onChange={(e) => setEditUser({...editUser, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>E-Mail</Label>
                <Input 
                  value={editUser.email || ''} 
                  onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                />
              </div>
              
              {/* 🆕 CUSTOMER ASSIGNMENT for legacy users */}
              {editUser.role !== 'superadmin' && (
                <div className="space-y-2">
                  <Label>Kundenzuordnung {!editUser.customer_id && <span className="text-red-600">*</span>}</Label>
                  {!editUser.customer_id && (
                    <p className="text-xs text-orange-600 mb-2">
                      ⚠️ Dieser User hat keine Kundenzuordnung und kann keine Standorte erstellen!
                    </p>
                  )}
                  <select 
                    value={editUser.customer_id || ''}
                    onChange={(e) => setEditUser({...editUser, customer_id: e.target.value})}
                    className="w-full h-10 rounded-md border border-input bg-background px-3"
                  >
                    <option value="">-- Kein Kunde zugewiesen --</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.company_name} ({c.customer_number})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Neues Passwort (leer lassen zum Behalten)</Label>
                <Input 
                  type="password"
                  value={editUser.password || ''} 
                  onChange={(e) => setEditUser({...editUser, password: e.target.value})}
                  placeholder="••••••••"
                />
              </div>
              <div className="space-y-2">
                <Label>Rolle</Label>
                <select 
                  value={editUser.role || 'employee'}
                  onChange={(e) => setEditUser({...editUser, role: e.target.value})}
                  className="w-full h-10 rounded-md border border-input bg-background px-3"
                >
                  <option value="superadmin">Superadmin</option>
                  <option value="admin">Admin (Pächter)</option>
                  <option value="employee">Mitarbeiter</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={editUser.is_active}
                  onChange={(e) => setEditUser({...editUser, is_active: e.target.checked})}
                  className="h-4 w-4"
                />
                <Label>Aktiv</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSaveEdit}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invitation Link Dialog */}
      <Dialog open={invitationDialogOpen} onOpenChange={setInvitationDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Einladungslink generiert
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3">
                <strong>User:</strong> {selectedUser?.name} ({selectedUser?.email})
              </p>
              <p className="text-sm text-gray-700 mb-3">
                <strong>Gültigkeit:</strong> 24 Stunden
              </p>
            </div>

            <div className="bg-white border border-gray-300 rounded-lg p-4">
              <Label className="text-sm font-medium mb-2 block">Einladungslink:</Label>
              <div className="flex gap-2">
                <Input 
                  value={invitationLink} 
                  readOnly 
                  className="font-mono text-xs"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={copyInvitationLink}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-xs text-orange-800">
                <strong>Hinweis:</strong> Senden Sie diesen Link an den User per E-Mail oder anderem sicheren Kanal.
                Der Link ist nur einmalig verwendbar.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setInvitationDialogOpen(false)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
